import { defineComponent, onMounted, ref, toRefs, watch } from '@vue/composition-api';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.gridlayer.googlemutant';
import { useNamespacedState } from 'vuex-composition-helpers';
import { PERSISTENCE } from '@/store/types/namespaces';
import { load } from './googleMaps';
import { generateClusterMarker, getMarkerIcon } from './helpers';
import { DEFAULT_STYLE, NIGHT_MODE_STYLE } from './styles';
import { DataMarker } from './types';
export default defineComponent({
    name: 'DpSimpleMap',
    props: {
        markers: { type: Array, default: () => [] },
        maxZoom: { type: Number, default: 17 },
        locked: { type: Boolean, default: false },
    },
    setup(props, { emit }) {
        const { markers, maxZoom, locked } = toRefs(props);
        const { uiDark } = useNamespacedState(PERSISTENCE, ['uiDark']);
        const mapElement = ref(null);
        let map = null;
        let cluster = null;
        let layerControl = null;
        let hasBuilt = false;
        const build = async () => {
            await load();
            if (hasBuilt)
                return Promise.resolve();
            // This should always exist when run after mounting
            if (!mapElement.value)
                return Promise.reject();
            map = L.map(mapElement.value, {
                attributionControl: false,
                layers: [],
                worldCopyJump: true,
                dragging: !L.Browser.mobile,
                scrollWheelZoom: !L.Browser.mobile,
            });
            const darkMode = uiDark.value;
            const styles = darkMode ? NIGHT_MODE_STYLE : DEFAULT_STYLE;
            const tileLayers = {
                Streets: L.gridLayer.googleMutant({ type: 'roadmap', styles }),
                Satellite: L.gridLayer.googleMutant({ type: 'satellite', styles }),
                Terrain: L.gridLayer.googleMutant({ type: 'terrain', styles }),
                Hybrid: L.gridLayer.googleMutant({ type: 'hybrid', styles }),
            };
            layerControl = L.control.layers({
                Streets: tileLayers.Streets,
                Satellite: tileLayers.Satellite,
                Hybrid: tileLayers.Hybrid,
                Terrain: tileLayers.Terrain,
            }, {}, { position: 'topleft' });
            layerControl.addTo(map);
            tileLayers.Streets.addTo(map);
            cluster = L.markerClusterGroup({
                chunkedLoading: true,
                maxClusterRadius: 40,
                showCoverageOnHover: true,
                removeOutsideVisibleBounds: true,
                iconCreateFunction: (group) => generateClusterMarker(group, {
                    useColour: true,
                    colouring: {
                        cluster: 'average',
                        clusterOpacity: 1,
                    },
                }),
            });
            map.addLayer(cluster);
            // Set default view
            map.setView([51.5, 0.12], 10);
            hasBuilt = true;
            return Promise.resolve();
        };
        onMounted(build);
        const fitBounds = (bounds) => {
            if (!map || !bounds.isValid())
                return;
            map.fitBounds(bounds, { maxZoom: maxZoom.value });
        };
        const centerMapOnMarkers = () => {
            if (!cluster)
                return;
            fitBounds(cluster.getBounds());
        };
        const handleMarkerClick = (marker) => {
            emit('marker-clicked', marker);
        };
        const updateMarkers = async (markersArray) => {
            await build();
            if (!cluster)
                return;
            cluster.clearLayers();
            const leafletMarkers = markersArray.map((m) => {
                const { latitude, longitude, icon, colour, } = m;
                const marker = new DataMarker(L.latLng(latitude, longitude), {
                    colour,
                });
                marker.bindTooltip(m.label);
                marker.setIcon(getMarkerIcon({
                    icon,
                    colour,
                }));
                marker.on('click', () => handleMarkerClick(m));
                return marker;
            });
            cluster.addLayers(leafletMarkers);
            centerMapOnMarkers();
        };
        onMounted(() => updateMarkers(markers.value));
        watch(markers, (newMarkersArray) => updateMarkers(newMarkersArray));
        const toggleLockedState = async (isLocked) => {
            await build();
            if (map === null)
                return;
            if (isLocked) {
                layerControl?.remove();
                map.zoomControl.remove();
            }
        };
        onMounted(() => toggleLockedState(locked.value));
        watch(locked, toggleLockedState);
        return {
            mapElement,
            centerMapOnMarkers,
            updateMarkers,
        };
    },
});
